import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Form, Input, Popconfirm, Radio, Select } from 'antd';
import InputNumber from 'antd/lib/input-number';
import { InfoCircleOutlined } from '@ant-design/icons';
import { CardMainLayout, Catcher, cardLayoutOptions } from '../../index';
import dayjs from 'dayjs';
import { IMonthlyRecurringCharges, IMonthlyRecurringChargesItem, NotificationType } from '../../../interfaces';
import { useCompanyInfo, useIsFreezingPeriod, useMonthlyRecurringChargesList } from '../../../hooks';
import {
    addNotification,
    apiAddFormErrorMessages,
    apiMessagesGetFormRules,
    deleteMonthlyRecurringChargesList,
    postMonthlyRecurringChargesList,
    putMonthlyRecurringChargesList,
} from '../../../middleware';
import { globalHexId, mapUi2ApiField } from '../../../instruments';
import { tB, tNM, tP } from '../../../translate';
import Styles from '../../styles/m_itemCardStyles.less';

const { Option } = Select;

interface IMonthlyRecurringChargesFrom extends IMonthlyRecurringChargesItem {
    discountDescription?: string;
    discountUnit?: string;
    discountAmount?: number;
}

interface IFormItem extends IMonthlyRecurringChargesFrom {
    remove?: (id: string | number, listId: string | number) => void;
    companyId: string;
    isFreezingPeriod: boolean;
}

function FormItem({ chargeType, remove, companyId, isFreezingPeriod, ...props }: IFormItem) {
    const [form] = Form.useForm();
    const [canSave, setCanSave] = useState(false);
    const [initialData, setInitialData] = useState<IMonthlyRecurringChargesFrom>();
    const [isDiscount, setIsDiscount] = useState(false);

    const isDefault = chargeType === undefined;
    const isPerImsi = chargeType === 'imsi';
    const isNew = typeof initialData?.id === 'string';

    const validationInputs = [...apiMessagesGetFormRules];

    const onFormChange = () => {
        const { id, ...props } = form.getFieldsValue();
        const { id: newId, ...initProps } = initialData || {};
        setCanSave(JSON.stringify(props) !== JSON.stringify(initProps));
    };

    const onSave = async () => {
        try {
            await form.validateFields();

            const values = form.getFieldsValue();

            console.log(values);

            console.log(initialData?.id);

            const formattedBody = mapUi2ApiField(values);

            if (isDiscount) {
                formattedBody.discount = {
                    amount:
                        formattedBody.discountUnit === 'currency'
                            ? `${values.discountAmount * 100 || 0}`
                            : values.discountAmount?.toString() || '0',
                    amount_type: values.discountUnit || 'percentage',
                    description: values.discountDescription || 'Discount',
                };

                delete formattedBody.discountDescription;
                delete formattedBody.discountUnit;
                delete formattedBody.discountAmount;
            } else {
                delete formattedBody.discount;
            }

            formattedBody.amount_cents = formattedBody.amount_cents * 100;

            let response;

            if (!isNew) {
                response = await putMonthlyRecurringChargesList(`${initialData?.id}`, companyId, formattedBody);
            } else {
                response = await postMonthlyRecurringChargesList(companyId, formattedBody);
            }

            const { isError, resp } = response;

            if (isError) {
                return apiAddFormErrorMessages(resp || {}, form);
            }

            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Monthly Recurring cost has been saved successfully',
            });

            const monthlyCharges = resp as unknown as IMonthlyRecurringChargesItem;

            setInitialData({ id: monthlyCharges.id, ...values });
            setCanSave(false);
        } catch (e) {
            console.error(e);
        }
    };

    const discountOnDelete = () => {
        setIsDiscount(false);
        form.setFieldValue('discountDescription', '');
        form.setFieldValue('discountUnit', '');
        form.setFieldValue('discountAmount', '');

        onFormChange();
    };

    const selectDiscountListJSX = [
        { id: 'percentage', name: '%' },
        { id: 'currency', name: 'EUR' },
    ].map(
        (s): JSX.Element => (
            <Option key={s.id} value={s.id}>
                <div>{s.name}</div>
            </Option>
        ),
    );

    useEffect(() => {
        if (props.discount) {
            setIsDiscount(true);

            const discountData = {
                discountDescription: props.discount.description || '',
                discountUnit: props.discount.amount_type || 'percentage',
                discountAmount:
                    props.discount.amount_type === 'percentage'
                        ? props.discount.amount
                        : parseFloat(props.discount.amount) / 100,
            };

            form.setFieldsValue(discountData);
        }
    }, [props.discount]);

    useEffect(() => {
        setInitialData(props);
    }, []);

    useEffect(() => {
        form.setFieldsValue(props);
    }, [JSON.stringify(props)]);

    return (
        <Form
            form={form}
            name={`${props.name}${props.id}`}
            initialValues={props}
            layout={'vertical'}
            className={`${isFreezingPeriod ? Styles.mrcFormFrozen : null} formStyle`}
            onValuesChange={onFormChange}
        >
            <div className={Styles.mrcFormItemContainer}>
                {!isDefault ? (
                    <>
                        <div className={Styles.mrcFormItemDefaultHeader}>
                            <h3>{isPerImsi ? 'Per Imsi' : 'Per Card'}:</h3>
                            <Form.Item name="name" rules={apiMessagesGetFormRules} style={{ margin: 0 }}>
                                <Radio.Group
                                    className={Styles.mrcFormItemDefaultHeaderRadioContainer}
                                    disabled={isFreezingPeriod}
                                >
                                    <Radio value={'all'}>All</Radio>
                                    {isPerImsi ? <Radio value={'active_cdr'}>Active this month</Radio> : null}
                                    <Radio value={isPerImsi ? 'active_status' : 'active_cdr'}>Active</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </div>
                        <Divider />
                    </>
                ) : (
                    <div>
                        <Form.Item name="name" label={'Name'} rules={apiMessagesGetFormRules}>
                            <Input className={Styles.mrcFormItemInput} disabled={isFreezingPeriod} />
                        </Form.Item>
                    </div>
                )}
                <div className={Styles.mrcFormItemAmountContainer}>
                    <Form.Item name="amountCents" label={'Amount'} rules={validationInputs} style={{ width: '100%' }}>
                        <InputNumber
                            step={1}
                            min={0.01}
                            precision={2}
                            className={Styles.mrcFormItemInput}
                            addonBefore={'EUR'}
                            disabled={isFreezingPeriod}
                        />
                    </Form.Item>
                    <div className={Styles.mrcFormItemBottomBar}>
                        <Button type={'primary'} disabled={!canSave || isFreezingPeriod} onClick={onSave}>
                            Save
                        </Button>
                        {isDefault && remove ? (
                            <Popconfirm
                                cancelText={tB('Cancel')}
                                disabled={isNew}
                                okText={tB('Delete')}
                                onConfirm={() => remove(initialData?.id || '', props.id)}
                                placement="top"
                                title={tNM('Confirm deletion.')}
                            >
                                {isNew ? (
                                    <Button
                                        danger
                                        onClick={() => remove(initialData?.id || '', props.id)}
                                        disabled={isFreezingPeriod}
                                    >
                                        {tB('Delete')}
                                    </Button>
                                ) : (
                                    <Button danger disabled={isFreezingPeriod}>
                                        {tB('Delete')}
                                    </Button>
                                )}
                            </Popconfirm>
                        ) : null}
                    </div>
                </div>
                {!isDiscount ? (
                    <Button
                        type={'primary'}
                        onClick={() => setIsDiscount(true)}
                        disabled={isFreezingPeriod}
                        style={{ marginBottom: '10px' }}
                    >
                        + Add Discount
                    </Button>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                        <Form.Item name="discountDescription" label={'Discount description'}>
                            <Input className={Styles.mrcFormItemInput} disabled={isFreezingPeriod} />
                        </Form.Item>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <Form.Item name="discountUnit" label={'Units'} style={{ flex: 1 }}>
                                <Select
                                    className={Styles.mrcFormItemInput}
                                    placeholder={'Unit'}
                                    disabled={isFreezingPeriod}
                                    defaultValue="percentage"
                                    onChange={(value) => {
                                        form.setFieldValue('discountUnit', value);
                                    }}
                                >
                                    {selectDiscountListJSX}
                                </Select>
                            </Form.Item>
                            <Form.Item name="discountAmount" label={'Amount'} style={{ flex: 1 }}>
                                <InputNumber
                                    step={1}
                                    min={0}
                                    className={Styles.mrcFormItemInput}
                                    disabled={isFreezingPeriod}
                                />
                            </Form.Item>
                            <Button
                                danger
                                onClick={discountOnDelete}
                                style={{ marginTop: '30px' }}
                                disabled={isFreezingPeriod}
                            >
                                Delete
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Form>
    );
}

export const EditCompanyFieldsMrcNrc: React.FC = observer(() => {
    const { id: companyId } = useCompanyInfo();
    const monthlyRecurringChargesList = useMonthlyRecurringChargesList();
    const isFreezingPeriod = useIsFreezingPeriod();
    const [newFormArray, setNewFormArray] = useState<IMonthlyRecurringCharges>([]);

    const mrcFormItem = useRef<HTMLDivElement>(null);

    const scrollBottom = () => {
        if (mrcFormItem.current) {
            const { scrollHeight } = mrcFormItem.current;
            mrcFormItem.current.scrollTop = scrollHeight;
        }
    };

    const addNewItem = () => {
        const id = globalHexId.getId();

        const initNewItemValue: IMonthlyRecurringChargesFrom = {
            amountCents: 0,
            id,
            name: '',
            chargeType: undefined,
        };

        setNewFormArray([...newFormArray, initNewItemValue]);
    };

    const removeItem = async (id: string | number, listId: string | number) => {
        console.log(id, listId);

        const isNew = typeof id === 'string';

        if (!isNew) {
            const { isError } = await deleteMonthlyRecurringChargesList(`${id}`, companyId);

            if (isError) {
                return addNotification({
                    type: NotificationType.ERROR,
                    message: 'An error has occurred while deleting',
                    isUsersError: true,
                });
            }
        }

        const filteredArr = newFormArray.filter((value) => value.id !== listId);

        setNewFormArray([...filteredArr]);
        if (!isNew) {
            addNotification({
                type: NotificationType.SUCCESS,
                message: 'Monthly Recurring cost has been deleted successfully',
                isUsersError: true,
            });
        }
    };

    useEffect(() => {
        const defaultValues = monthlyRecurringChargesList.filter((value) => value.chargeType);
        const customValues = monthlyRecurringChargesList.filter((value) => !value.chargeType);

        setNewFormArray([...defaultValues, ...customValues]);
    }, [JSON.stringify(monthlyRecurringChargesList)]);

    useEffect(() => {
        scrollBottom();
    }, [newFormArray]);

    return (
        <Catcher>
            <CardMainLayout
                name={tP('Monthly Recurring Costs')}
                columnOption={cardLayoutOptions.mrcEditForm}
                idSpinners={[
                    'GET_COMPANY_MONTHLY_PAYMENTS',
                    'POST_COMPANY_MONTHLY_PAYMENTS',
                    'PUT_COMPANY_MONTHLY_PAYMENTS',
                    'DELETE_COMPANY_MONTHLY_PAYMENTS',
                ]}
            >
                {isFreezingPeriod ? (
                    <p className={Styles.mrcFormFrozenMessage}>
                        <InfoCircleOutlined style={{ marginRight: '5px' }} />
                        Editing will be unlocked on the 5th of {dayjs().format('MMMM')}.
                    </p>
                ) : null}
                <div className={Styles.mrcFormItem} ref={mrcFormItem}>
                    {newFormArray.map((field: IMonthlyRecurringChargesItem) => {
                        return (
                            <div key={field.id}>
                                <FormItem
                                    {...field}
                                    remove={removeItem}
                                    companyId={companyId}
                                    isFreezingPeriod={isFreezingPeriod}
                                />
                            </div>
                        );
                    })}
                </div>
                <Button type="dashed" onClick={addNewItem} className={Styles.mrcAddButton} disabled={isFreezingPeriod}>
                    Add New
                </Button>
            </CardMainLayout>
        </Catcher>
    );
});
